import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getMessageCounter, getSuperPresence, getSuperTotalMessages, getSystemClock } from '../lib/api';
import Odometer from './Odometer';
import { toast } from 'sonner';
import MaintenanceBanner from './MaintenanceBanner';
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Contact2,
  Send,
  History,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Megaphone,
  Mail,
  ChevronDown,
  ChevronRight,
  Layers,
  User,
  Loader2,
  Trash2,
  Sun,
  Moon,
  FileText,
  AlertTriangle,
  AlertCircle,
  CreditCard,
  Database,
  FlaskConical,
  Info,
  Wrench,
  Radio,
  Server,
  Ban,
  Smartphone,
  Clock,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { cn } from '../lib/utils';

const adminSubmenuItems = [
  { path: '/whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { path: '/users', label: 'Usuários', icon: Users },
  { path: '/subscription', label: 'Minha Assinatura', icon: CreditCard },
  { path: '/meus-dados', label: 'Meus Dados', icon: Database },
];

const navItems = {
  SUPER_ADMIN: [
    { path: '/admins', label: 'Administradores', icon: Shield },
    { path: '/smtp', label: 'SMTP', icon: Mail },
    { path: '/cms', label: 'CMS', icon: FileText },
    { path: '/stripe', label: 'Stripe', icon: CreditCard },
    { path: '/pat-repository', label: 'Servidores WhatsApp', icon: Server },
    { path: '/mensagens-live', label: 'Mensagens Live', icon: Radio },
    { path: '/celulares-em-uso', label: 'Celulares em Uso', icon: Smartphone },
    { path: '/info-system', label: 'Info Sistema', icon: Info },
    { path: '/purge', label: 'Purga', icon: Trash2 },
    { path: '/maintenance', label: 'Manutenção', icon: Wrench },
    { path: '/blocked-accounts', label: 'Contas Bloqueadas', icon: Ban },
    { path: '/billing-tests', label: 'Testes do Sistema', icon: FlaskConical },
  ],
  ADMIN: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/contacts', label: 'Contatos', icon: Contact2 },
    { path: '/groups', label: 'Grupos', icon: Layers },
    { path: '/send', label: 'Enviar Mensagem', icon: Send },
    { path: '/campaigns', label: 'Campanhas', icon: Megaphone },
    { path: '/messages', label: 'Histórico', icon: History },
  ],
  USER_EDITOR: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/contacts', label: 'Contatos', icon: Contact2 },
    { path: '/groups', label: 'Grupos', icon: Layers },
    { path: '/send', label: 'Enviar Mensagem', icon: Send },
    { path: '/campaigns', label: 'Campanhas', icon: Megaphone },
    { path: '/messages', label: 'Histórico', icon: History },
  ],
  USER_SEND: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/contacts', label: 'Contatos', icon: Contact2 },
    { path: '/send', label: 'Enviar Mensagem', icon: Send },
    { path: '/campaigns', label: 'Campanhas', icon: Megaphone },
    { path: '/messages', label: 'Histórico', icon: History },
  ],
  USER_READ: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/contacts', label: 'Contatos', icon: Contact2 },
    { path: '/campaigns', label: 'Campanhas', icon: Megaphone },
    { path: '/messages', label: 'Histórico', icon: History },
  ],
};

export default function Layout({ children }) {
  const { user, logout, isSuperAdmin, isImpersonating, stopImpersonating, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messageCounter, setMessageCounter] = useState(null);
  const [presenceData, setPresenceData] = useState(null);
  const [totalMessages, setTotalMessages] = useState(null);
  const [clockInfo, setClockInfo] = useState(null); // relógio interno (só em Info Sistema)
  const [clockTick, setClockTick] = useState(0);    // força o re-render a cada segundo
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', email: '', whatsapp: '+55', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageEdited, setImageEdited] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [profilePhoneStatus, setProfilePhoneStatus] = useState(null);

  const items = navItems[user?.role] || [];

  // ---- Onboarding do WhatsApp -------------------------------------------------------
  // Conta que nunca conectou um número: o dono é levado UMA VEZ POR ENTRADA até a tela do
  // QR e vê um balão preso ao item WhatsApp do menu. Assim que a primeira conexão é
  // confirmada, o backend para de mandar a pendência e os dois somem para sempre.
  // Fica de fora quem não pode agir (usuário comum, Super Admin) e quem já tem uma tela
  // própria falando com ele (trial expirado, inadimplente, cancelada).
  const contaBloqueada = !!(user?.trial_blocked || user?.payment_blocked || user?.subscription_canceled);
  // Só o dono pode agir: o item WhatsApp nem existe no menu dos demais usuários.
  const podeVerBalao =
    user?.role === 'ADMIN' && !!user?.is_tenant_owner && !isSuperAdmin && !contaBloqueada;

  const waOnboarding = !!user?.whatsapp_onboarding_pending && podeVerBalao;

  // ---- Queda do WhatsApp ------------------------------------------------------------
  // Conta que JÁ conectou e caiu. Aparece a cada entrada enquanto a queda durar, mas NÃO
  // leva ninguém para lugar nenhum: é cliente antigo, que pode ter entrado para ver
  // histórico ou campanha, e ser jogado no QR todo login seria intrusivo.
  // Vermelho, e não âmbar: aqui não falta terminar uma configuração, aqui QUEBROU e nada
  // está sendo enviado.
  const waQueda = !!user?.whatsapp_reconnect_pending && podeVerBalao;

  // useMemo OBRIGATÓRIO: este objeto é dependência de efeito. Recriado a cada render, ele
  // fazia o efeito da medida rodar sempre, o setBalaoPos gravar um objeto novo, disparar
  // outro render, e assim por diante: laço infinito que travava a aplicação inteira (a tela
  // parava de responder a cliques do menu). Só acontecia em conta COM balão.
  const balao = useMemo(
    () =>
      waOnboarding
        ? {
            cor: 'ambar',
            titulo: 'Conecte seu WhatsApp',
            texto: 'Para enviar mensagens, você precisa conectar o WhatsApp do seu celular.',
          }
        : waQueda
          ? {
              cor: 'vermelho',
              titulo: 'WhatsApp desconectado',
              texto: 'Suas mensagens não estão sendo enviadas. Clique aqui para reconectar.',
            }
          : null,
    [waOnboarding, waQueda]
  );

  // O balão precisa FLUTUAR sobre o conteúdo, à direita da barra. Ele não pode ser filho do
  // menu: o <nav> tem rolagem própria (corta o que vaza) e a barra tem transform, que faz até
  // 'position: fixed' ser cortado por ela. Por isso ele vai por portal no body e é posicionado
  // pela medida do item do menu, refeita em rolagem, redimensionamento e abertura do submenu.
  const waItemRef = useRef(null);
  const [balaoPos, setBalaoPos] = useState(null);

  const medirBalao = useCallback(() => {
    const el = waItemRef.current;
    if (!el) { setBalaoPos(null); return; }
    const r = el.getBoundingClientRect();
    // Barra escondida (celular) ou item fora da área visível: sem balão.
    if (!r.width || r.right <= 0 || r.top <= 0 || r.bottom > window.innerHeight) {
      setBalaoPos(null);
      return;
    }
    // Só grava quando a posição REALMENTE mudou. Gravar um objeto novo com os mesmos números
    // dispara render à toa e, se algum dia este medidor voltar a rodar a cada render, é esta
    // guarda que impede o laço infinito de novo.
    const top = r.top + r.height / 2;
    const left = r.right + 16;
    setBalaoPos((anterior) =>
      anterior && anterior.top === top && anterior.left === left ? anterior : { top, left }
    );
  }, []);

  useEffect(() => {
    if (!balao) { setBalaoPos(null); return; }
    medirBalao();
    const refazer = () => medirBalao();
    window.addEventListener('resize', refazer);
    window.addEventListener('scroll', refazer, true); // captura a rolagem do próprio menu
    return () => {
      window.removeEventListener('resize', refazer);
      window.removeEventListener('scroll', refazer, true);
    };
  }, [balao, adminMenuOpen, sidebarOpen, location.pathname, medirBalao]);

  // O submenu Admin nasce fechado; com pendência ele precisa nascer aberto, senão o balão
  // apontaria para um item que não está na tela.
  useEffect(() => {
    if (balao) setAdminMenuOpen(true);
  }, [balao]);

  // Uma vez por entrada, não a cada navegação: a marca é de sessão do navegador (some ao
  // fechar a aba e é apagada no logout). Sem ela, a pessoa seria trazida de volta ao QR toda
  // vez que tentasse abrir Contatos ou Campanhas, e ficaria presa.
  useEffect(() => {
    if (!waOnboarding) return;
    if (sessionStorage.getItem('wa_onboarding_redirected')) return;
    sessionStorage.setItem('wa_onboarding_redirected', '1');
    if (location.pathname !== '/whatsapp') navigate('/whatsapp');
    // eslint-disable-next-line
  }, [waOnboarding]);

  // Fetch message counter for non-super admin users
  useEffect(() => {
    if (!isSuperAdmin && user) {
      fetchMessageCounter();
      // Refresh every 30 seconds
      const interval = setInterval(fetchMessageCounter, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isSuperAdmin]);

  const fetchMessageCounter = async () => {
    try {
      const response = await getMessageCounter();
      setMessageCounter(response.data);
    } catch (error) {
      console.error('Error fetching message counter:', error);
    }
  };

  // Super Admin presence indicator: polls only when on /admins and the tab is visible.
  useEffect(() => {
    if (!isSuperAdmin || location.pathname !== '/admins' || !user) {
      setPresenceData(null);
      return;
    }
    const fetchPresence = async () => {
      try {
        const res = await getSuperPresence();
        setPresenceData(res.data);
      } catch (e) {
        // silent — next tick retries
      }
    };
    fetchPresence();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchPresence();
    }, 15000);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchPresence();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isSuperAdmin, location.pathname, user]);

  // Super Admin lifetime total messages: polls only when on /mensagens-live.
  useEffect(() => {
    if (!isSuperAdmin || location.pathname !== '/mensagens-live' || !user) {
      setTotalMessages(null);
      return;
    }
    const fetchTotal = async () => {
      try {
        const res = await getSuperTotalMessages();
        setTotalMessages(res.data.total || 0);
      } catch (e) {
        // silent — next tick retries
      }
    };
    fetchTotal();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchTotal();
    }, 3000);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchTotal();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isSuperAdmin, location.pathname, user]);

  // Relógio interno do sistema: só na tela Info Sistema. Busca o instante do SERVIDOR,
  // guarda a diferença para o relógio do navegador e conta os segundos localmente, então o
  // ponteiro anda de 1 em 1 segundo sem uma requisição por segundo. Ressincroniza a cada 60s.
  useEffect(() => {
    if (!isSuperAdmin || location.pathname !== '/info-system' || !user) {
      setClockInfo(null);
      return;
    }
    let vivo = true;
    const sincronizar = async () => {
      const enviadoEm = Date.now();
      try {
        const res = await getSystemClock();
        if (!vivo) return;
        const retornoEm = Date.now();
        // desconta metade da ida e volta da rede para não acusar diferença que não existe
        const offsetMs = new Date(res.data.utc).getTime() + (retornoEm - enviadoEm) / 2 - retornoEm;
        setClockInfo({ ...res.data, offsetMs });
      } catch (e) {
        // silencioso: a próxima sincronização tenta de novo
      }
    };
    sincronizar();
    const idSync = setInterval(() => {
      if (document.visibilityState === 'visible') sincronizar();
    }, 60000);
    const idTick = setInterval(() => {
      if (document.visibilityState === 'visible') setClockTick((t) => t + 1);
    }, 1000);
    return () => {
      vivo = false;
      clearInterval(idSync);
      clearInterval(idTick);
    };
  }, [isSuperAdmin, location.pathname, user]);

  // Leitura viva: reaplica a diferença medida a cada tique. O dia do sistema vem do corte
  // que o backend informa (UTC ou BRT), por isso ele aparece diferente do dia no Brasil
  // enquanto o corte for em UTC.
  const relogio = useMemo(() => {
    if (!clockInfo) return null;
    const agora = new Date(Date.now() + clockInfo.offsetMs);
    const emBrt = (opcoes) =>
      new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', ...opcoes }).format(agora);
    const emUtc = (opcoes) =>
      new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', ...opcoes }).format(agora);
    const diaBrasil = emBrt({ day: '2-digit', month: '2-digit' });
    const diaSistema = clockInfo.corte === 'BRT' ? diaBrasil : emUtc({ day: '2-digit', month: '2-digit' });
    const dataSistema = clockInfo.corte === 'BRT'
      ? emBrt({ day: '2-digit', month: '2-digit', year: 'numeric' })
      : emUtc({ day: '2-digit', month: '2-digit', year: 'numeric' });
    return {
      hora: emBrt({ day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit',
                    minute: '2-digit', second: '2-digit', hour12: false }),
      horaUtc: emUtc({ day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }),
      diaBrasil,
      diaSistema,
      dataSistema,
      // hora NA REFERÊNCIA DO SISTEMA (hoje UTC): é o que faz a divergência aparecer,
      // porque a pílula acima mostra a hora do Brasil no mesmo instante.
      horaSegundos: (clockInfo.corte === 'BRT' ? emBrt : emUtc)(
        { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
      divergente: diaSistema !== diaBrasil,
      difNavegador: Math.round(clockInfo.offsetMs / 1000),
    };
  }, [clockInfo, clockTick]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenProfile = () => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
      whatsapp: user?.whatsapp || '+55',
      password: '',
    });
    setImageFile(null);
    // Se o usuário tem avatar_url, usa a URL completa para preview
    if (user?.avatar_url) {
      setImagePreview(getAvatarDisplayUrl(user.avatar_url));
    } else {
      setImagePreview(null);
    }
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageEdited(false);
    setImageRemoved(false);
    setProfilePhoneStatus(null);
    setProfileDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagem muito grande. Máximo: 5MB');
        return;
      }
      
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setImageEdited(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
      setImageEdited(true);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageEdited(false);
    setImageRemoved(true);
  };

  const getCroppedImage = () => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 200; // Tamanho final da imagem
      canvas.width = size;
      canvas.height = size;

      const img = new Image();
      img.onload = () => {
        // Criar círculo de recorte
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        // Calcular dimensões mantendo proporção
        const previewSize = 192; // w-48
        const ratio = size / previewSize;
        
        // Dimensões da imagem no preview
        let imgWidth = img.width;
        let imgHeight = img.height;
        
        // Redimensionar para caber no preview
        if (imgWidth > imgHeight) {
          imgHeight = (imgHeight / imgWidth) * previewSize;
          imgWidth = previewSize;
        } else {
          imgWidth = (imgWidth / imgHeight) * previewSize;
          imgHeight = previewSize;
        }
        
        // Aplicar zoom
        imgWidth *= zoom;
        imgHeight *= zoom;
        
        // Converter para tamanho do canvas
        imgWidth *= ratio;
        imgHeight *= ratio;
        
        // Posição com ratio
        const x = size / 2 - imgWidth / 2 + position.x * ratio;
        const y = size / 2 - imgHeight / 2 + position.y * ratio;

        ctx.drawImage(img, x, y, imgWidth, imgHeight);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.9);
      };
      img.src = imagePreview;
    });
  };

  const getAvatarDisplayUrl = (avatarUrl) => {
    if (!avatarUrl) return null;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}${avatarUrl}?t=${Date.now()}`;
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    // Valida só o formato do número (não verificamos mais existência no WhatsApp).
    const cleaned = profileData.whatsapp?.replace(/[\s\-()]/g, '') || '';
    if (cleaned && cleaned !== '+55') {
      const phoneRegex = /^\+\d{10,15}$/;
      if (!phoneRegex.test(cleaned)) {
        setProfilePhoneStatus('format_error');
        toast.error('Número inválido. Use o formato internacional: +5511999999999');
        return;
      }
      setProfilePhoneStatus(null);
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: profileData.name,
        email: profileData.email,
        whatsapp: profileData.whatsapp,
      };

      // Só envia senha se foi preenchida
      if (profileData.password) {
        updateData.password = profileData.password;
      }

      // 1. Salvar dados do perfil (sem avatar)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao atualizar perfil');
      }

      const result = await response.json();
      if (result.token) {
        localStorage.setItem('token', result.token);
      }

      let newAvatarUrl = user?.avatar_url || null;

      // 2. Se a imagem foi removida, chamar DELETE
      if (imageRemoved) {
        const delResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (delResp.ok) {
          newAvatarUrl = null;
        }
      }
      // 3. Se a imagem foi alterada, fazer upload via POST com FormData
      else if (imagePreview && (imageFile || imageEdited)) {
        const blob = await getCroppedImage();
        const formData = new FormData();
        formData.append('file', blob, 'avatar.jpg');

        const uploadResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (uploadResp.ok) {
          const uploadResult = await uploadResp.json();
          newAvatarUrl = uploadResult.avatar_url;
        }
      }

      // Atualizar o user no contexto com os novos dados
      const updatedUser = {
        ...user,
        name: profileData.name,
        email: profileData.email,
        whatsapp: profileData.whatsapp,
        avatar_url: newAvatarUrl,
      };

      updateUser(updatedUser);

      toast.success('Perfil atualizado com sucesso!');
      setProfileDialogOpen(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleName = (role) => {
    const names = {
      SUPER_ADMIN: 'Super Admin',
      ADMIN: 'Administrador',
      USER_EDITOR: 'Editor',
      USER_SEND: 'Envio',
      USER_READ: 'Somente Leitura',
    };
    return names[role] || role;
  };

  const handleStopImpersonating = async () => {
    const success = await stopImpersonating();
    if (success) {
      toast.success('Voltou à sua conta de Super Admin');
      // Usar window.location para forçar reload completo
      window.location.href = '/admins';
    } else {
      toast.error('Erro ao voltar. Você será desconectado.');
    }
  };


  const getCounterColor = () => {
    if (!messageCounter || messageCounter.unlimited || user?.subscription_canceled) return 'bg-teal-100 text-teal-800';
    const percentage = (messageCounter.sent / messageCounter.limit) * 100;
    if (percentage >= 90) return 'bg-red-100 text-red-800';
    if (percentage >= 75) return 'bg-amber-100 text-amber-800';
    return 'bg-teal-100 text-teal-800';
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <MaintenanceBanner />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[280px] flex flex-col border-r bg-card transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Watizap</h1>
            <p className="text-xs text-muted-foreground">Seu zap turbinado</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto md:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-secondary text-foreground border-l-[3px] border-primary'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  )}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Admin submenu - only for ADMIN role */}
            {user?.role === 'ADMIN' && (
              <div>
                <button
                  onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    adminSubmenuItems.some(item => location.pathname === item.path)
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5" />
                    <span>Admin</span>
                  </div>
                  {adminMenuOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {adminMenuOpen && (
                  <div className="mt-1 space-y-1 ml-4">
                    {adminSubmenuItems
                      .filter((item) => !['/subscription', '/meus-dados'].includes(item.path) || user?.is_tenant_owner)
                      .map((item) => {
                      const isActive = location.pathname === item.path;
                      const comBalao = !!balao && item.path === '/whatsapp';
                      return (
                        <React.Fragment key={item.path}>
                          <Link
                            ref={comBalao ? waItemRef : undefined}
                            to={item.path}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                              isActive
                                ? 'bg-secondary text-foreground border-l-[3px] border-primary'
                                : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                              comBalao && !isActive && (balao.cor === 'vermelho'
                                ? 'text-red-700 dark:text-red-400'
                                : 'text-amber-700 dark:text-amber-400')
                            )}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-${item.path.replace('/', '')}`}
                          >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>

      {/* Balão preso ao item WhatsApp: portal no body para poder invadir a área de conteúdo,
          à direita da barra, sem ser cortado pela rolagem do menu. Duas variantes, nunca as
          duas ao mesmo tempo: âmbar para quem nunca conectou, vermelho para quem caiu. */}
      {balao && balaoPos && createPortal(
        <button
          type="button"
          onClick={() => navigate('/whatsapp')}
          style={{ top: balaoPos.top, left: balaoPos.left, transform: 'translateY(-50%)' }}
          className={cn(
            'balao-flutuante fixed z-[60] w-64 rounded-xl border p-3 text-left shadow-xl transition-colors dark:shadow-black/40',
            balao.cor === 'vermelho'
              ? 'border-red-300 bg-red-50 shadow-red-900/10 hover:bg-red-100 dark:border-red-900/60 dark:bg-red-950 dark:hover:bg-red-900'
              : 'border-amber-300 bg-amber-50 shadow-amber-900/10 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950 dark:hover:bg-amber-900'
          )}
          data-testid="wa-balao-menu"
        >
          {/* bg-inherit (e não uma cor fixa): metade da setinha fica DENTRO do balão, então
              com cor própria ela aparecia como um quadradinho claro quando o balão escurecia
              no hover. ⚠️ Por isso o fundo do balão precisa ser OPACO: com cor translúcida, a
              metade de dentro pinta a mesma camada duas vezes e o quadradinho reaparece. */}
          <span
            className={cn(
              'absolute -left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 border-b border-l bg-inherit',
              balao.cor === 'vermelho'
                ? 'border-red-300 dark:border-red-900/60'
                : 'border-amber-300 dark:border-amber-900/60'
            )}
          />
          <span className="flex items-start gap-2">
            <Smartphone
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0',
                balao.cor === 'vermelho'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-amber-600 dark:text-amber-400'
              )}
            />
            <span
              className={cn(
                'text-xs leading-snug',
                balao.cor === 'vermelho'
                  ? 'text-red-900 dark:text-red-200'
                  : 'text-amber-900 dark:text-amber-200'
              )}
            >
              <strong className="block text-sm">{balao.titulo}</strong>
              {balao.texto}
            </span>
          </span>
        </button>,
        document.body
      )}

      {/* Impersonation Banner - Bottom of Sidebar */}
        {isImpersonating && (
          <div className="border-t bg-amber-500 text-white">
            <div className="p-3">
              <div className="flex items-center justify-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <p className="text-xs font-semibold">
                  Modo de Impersonação
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleStopImpersonating}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                data-testid="stop-impersonating-button"
              >
                <X className="w-3 h-3 mr-1.5" />
                Voltar ao Super Admin
              </Button>
            </div>
          </div>
        )}

        {/* User info */}
        <div className="border-t p-4">
          <div 
            className="flex items-center gap-3 mb-3 cursor-pointer hover:bg-secondary p-2 rounded-lg transition-colors"
            onClick={handleOpenProfile}
          >
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              {user?.avatar_url ? (
                <img src={getAvatarDisplayUrl(user.avatar_url)} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <span className="text-sm font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{getRoleName(user?.role)}</p>
            </div>
          </div>
          
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
          >
            {theme === 'light' ? (
              <>
                <Moon className="w-4 h-4 mr-2" />
                Tema Escuro
              </>
            ) : (
              <>
                <Sun className="w-4 h-4 mr-2" />
                Tema Claro
              </>
            )}
          </Button>
          
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex flex-col h-screen flex-1 md:ml-[280px] overflow-hidden">
        {/* Topbar */}
        <header className="sticky top-0 z-30 topbar flex-shrink-0" data-testid="topbar">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="hidden md:block">
              <h2 className="text-lg font-semibold">
                {items.find((i) => i.path === location.pathname)?.label || 
                 adminSubmenuItems.find((i) => i.path === location.pathname)?.label ||
                 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {/* Relógio interno (só em Info Sistema): a hora do SERVIDOR e, principalmente,
                  qual dia ele está usando para cortar as contagens. */}
              {isSuperAdmin && location.pathname === '/info-system' && relogio && (
                <div
                  className="hidden sm:flex flex-col items-center gap-1"
                  title={[
                    `Fuso do aplicativo: ${clockInfo.fuso_app}`,
                    clockInfo.fuso_so ? `Fuso do servidor: ${clockInfo.fuso_so}` : null,
                    clockInfo.ntp === null ? null : `NTP: ${clockInfo.ntp ? 'sincronizado' : 'fora de sincronia'}`,
                    `UTC agora: ${relogio.horaUtc}`,
                    `Mês usado na cota: ${clockInfo.mes_cota}`,
                    `Diferença para o relógio do seu navegador: ${relogio.difNavegador}s`,
                  ].filter(Boolean).join('\n')}
                  data-testid="system-clock"
                >
                  <div
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
                      relogio.divergente
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    )}
                  >
                    <Clock className="w-4 h-4" />
                    <span className="tabular-nums">{relogio.hora}</span>
                    <span className="text-xs opacity-70">GMT-3</span>
                  </div>
                  <span
                    className={cn(
                      'text-xs tabular-nums',
                      relogio.divergente ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
                    )}
                  >
                    {`Data do sistema ${relogio.dataSistema}, ${relogio.horaSegundos}`}
                  </span>
                  {Math.abs(relogio.difNavegador) > 60 && (
                    <span className="text-xs text-red-600 dark:text-red-400">
                      Relógio do servidor {relogio.difNavegador > 0 ? 'adiantado' : 'atrasado'} em {Math.abs(relogio.difNavegador)}s
                    </span>
                  )}
                </div>
              )}
              {/* Super Admin presence indicator (only on /admins) */}
              {isSuperAdmin && location.pathname === '/admins' && presenceData && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  <span className="inline-block h-2 w-2 rounded-full bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.7)] animate-pulse" />
                  <span>{presenceData.total_online.toLocaleString('pt-BR')} online</span>
                </div>
              )}
              {/* Super Admin lifetime total (only on /mensagens-live) — stat tile, no pill */}
              {isSuperAdmin && location.pathname === '/mensagens-live' && totalMessages !== null && (
                <div className="flex items-center gap-3">
                  <span className="h-8 w-px bg-border" aria-hidden />
                  <div className="flex flex-col items-end leading-none">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1">
                      Total processadas
                    </span>
                    <span className="text-2xl font-bold tabular-nums text-gray-700 dark:text-gray-300">
                      <Odometer value={totalMessages} />
                    </span>
                  </div>
                </div>
              )}
              {/* Message Counter */}
              {!isSuperAdmin && messageCounter && (
                <div className="flex flex-col items-center gap-1">
                  <div 
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium pointer-events-none",
                      getCounterColor()
                    )}
                  >
                    <Mail className="w-4 h-4" />
                    <span>
                      {(messageCounter.unlimited || user?.subscription_canceled)
                        ? `${messageCounter.sent.toLocaleString()} enviadas`
                        : `${messageCounter.sent.toLocaleString()} / ${messageCounter.limit.toLocaleString()}`
                      }
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 pt-3 overflow-y-auto" data-testid="page-content">
          {children}
        </main>
      </div>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Perfil</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveProfile}>
            <div className="flex flex-row-reverse gap-6 py-4">
              {/* Avatar Upload - Left Side */}
              <div className="space-y-2 shrink-0 w-72">
                <Label>Foto de Perfil</Label>
                <div className="flex flex-col items-center gap-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="cursor-pointer text-xs"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Recomendado: 200x200px. Máx: 5MB
                  </p>
                  
                  {imagePreview && (
                    <div className="space-y-3 w-full">
                      <div 
                        className="relative w-40 h-40 mx-auto border-2 border-dashed rounded-full overflow-hidden cursor-move bg-secondary"
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                      >
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="absolute pointer-events-none"
                          style={{
                            transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${zoom})`,
                            left: '50%',
                            top: '50%',
                            transformOrigin: 'center',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs whitespace-nowrap">Zoom:</Label>
                          <input
                            type="range"
                            min="0.5"
                            max="3"
                            step="0.1"
                            value={zoom}
                            onChange={(e) => {
                              setZoom(parseFloat(e.target.value));
                              setImageEdited(true);
                            }}
                            className="flex-1"
                          />
                          <span className="text-xs text-muted-foreground w-12">{Math.round(zoom * 100)}%</span>
                        </div>
                        <p className="text-xs text-center text-muted-foreground">
                          Arraste a imagem para reposicionar
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={handleRemoveImage}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remover Imagem
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Form Fields - Right Side */}
              <div className="flex-1 space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="profile-name">Nome</Label>
                <Input
                  id="profile-name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  required
                  disabled={user?.is_tenant_owner}
                />
                {user?.is_tenant_owner && (
                  <p className="text-xs text-muted-foreground">
                    A conta de email do administrador não pode ser alterada.
                  </p>
                )}
              </div>

              {/* WhatsApp - hidden for read-only, send-only and super admin users */}
              {user?.role !== 'USER_READ' && user?.role !== 'USER_SEND' && !isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="profile-whatsapp">Número de WhatsApp <span className="text-xs text-muted-foreground font-normal">(ex: +5521999999999)</span></Label>
                <div className="relative">
                  <Input
                    id="profile-whatsapp"
                    type="tel"
                    value={profileData.whatsapp}
                    onChange={(e) => {
                      setProfileData({ ...profileData, whatsapp: e.target.value });
                      setProfilePhoneStatus(null);
                    }}
                    placeholder="+5511999999999"
                    data-testid="profile-whatsapp-input"
                    className={
                      profilePhoneStatus === 'format_error' ? 'border-amber-500 pr-8' : ''
                    }
                  />
                  {profilePhoneStatus === 'format_error' && (
                    <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                  )}
                </div>
                {profilePhoneStatus === 'format_error' && (
                  <p className="text-xs text-amber-600">Formato inválido. Use: +5511999999999</p>
                )}
              </div>
              )}

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="profile-password">Nova Senha (deixe em branco para manter)</Label>
                <Input
                  id="profile-password"
                  type="password"
                  value={profileData.password}
                  onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProfileDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
